import React, { memo } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view';

// Form values interface
export interface CategoriesFormValues {
  excludedCategories: number[];
}

// Tree structure types
export interface CategoryTreeItem {
  id: string;
  name: string;
  classificationId: number;
}

export interface ParentGroupTreeItem {
  id: string;
  name: string;
  isAsset: boolean;
  categories: CategoryTreeItem[];
}

interface CategoryItemProps {
  category: CategoryTreeItem;
  initialIsExcluded: boolean;
  onCategoryToggle: (classificationId: number) => void;
}

interface ParentGroupItemProps {
  parentGroup: ParentGroupTreeItem;
  initialExcludedChildrenCount: number;
  totalChildren: number;
  initialExcludedCategories: number[];
  onCategoryToggle: (classificationId: number) => void;
  onParentGroupToggle: (isAsset: boolean, categoryIds: number[]) => void;
}

interface RenderCategoriesTreeProps {
  treeStructure: ParentGroupTreeItem[];
  initialFormikValues: CategoriesFormValues;
  onCategoryToggle: (classificationId: number) => void;
  onParentGroupToggle: (isAsset: boolean, categoryIds: number[]) => void;
}

const CategoryItem = memo(
  ({ category, initialIsExcluded, onCategoryToggle }: CategoryItemProps) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onCategoryToggle(category.classificationId);
    };

    return (
      <TreeItem
        key={category.id}
        itemId={category.id}
        sx={{
          '& .MuiTreeItem-iconContainer': {
            width: '4px',
          },
        }}
        label={
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            gap={1}
            sx={{ width: '100%' }}
          >
            <Typography variant='body2'>{category.name}</Typography>
            <Checkbox
              id={`excludedCategories-${category.classificationId}`}
              name='excludedCategories'
              checked={!initialIsExcluded}
              onChange={handleChange}
              onClick={(e) => {
                e.stopPropagation();
              }}
              size='small'
            />
          </Box>
        }
      />
    );
  },
);
CategoryItem.displayName = 'CategoryItem';

const ParentGroupItem = memo(
  ({
    parentGroup,
    initialExcludedChildrenCount,
    totalChildren,
    initialExcludedCategories,
    onCategoryToggle,
    onParentGroupToggle,
  }: ParentGroupItemProps) => {
    // Calculate checkbox state based on children:
    // - If all children are included (excludedChildrenCount === 0) → checked
    // - If all children are excluded (excludedChildrenCount === totalChildren) → unchecked
    // - If some children are excluded (0 < excludedChildrenCount < totalChildren) → indeterminate
    const isChecked = initialExcludedChildrenCount === 0;
    const isIndeterminate =
      initialExcludedChildrenCount > 0 &&
      initialExcludedChildrenCount < totalChildren;

    // Get all category IDs in this parent group
    const categoryIds = parentGroup.categories.map(
      (category) => category.classificationId,
    );

    const handleParentGroupChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      e.stopPropagation();
      // Toggle all children categories when parent group checkbox is clicked
      onParentGroupToggle(parentGroup.isAsset, categoryIds);
    };

    return (
      <TreeItem
        key={parentGroup.id}
        itemId={parentGroup.id}
        label={
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            gap={1}
          >
            <Typography
              variant='body2'
              sx={{ fontWeight: 600 }}
            >
              {parentGroup.name}
            </Typography>
            <Checkbox
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={handleParentGroupChange}
              onClick={(e) => {
                e.stopPropagation();
              }}
              size='small'
            />
          </Box>
        }
      >
        {parentGroup.categories.map((category) => {
          const isCategoryExcluded = initialExcludedCategories.includes(
            category.classificationId,
          );
          return (
            <CategoryItem
              key={category.id}
              category={category}
              initialIsExcluded={isCategoryExcluded}
              onCategoryToggle={onCategoryToggle}
            />
          );
        })}
      </TreeItem>
    );
  },
);
ParentGroupItem.displayName = 'ParentGroupItem';

// Render tree - re-renders when Formik values change to update checkbox states
export const RenderCategoriesTree = memo(
  ({
    treeStructure,
    initialFormikValues,
    onCategoryToggle,
    onParentGroupToggle,
  }: RenderCategoriesTreeProps) => {
    if (!treeStructure.length) return null;

    return (
      <>
        {treeStructure.map((parentGroup) => {
          // Calculate current state from Formik values
          const excludedChildrenCount = parentGroup.categories.filter(
            (category) =>
              initialFormikValues.excludedCategories.includes(
                category.classificationId,
              ),
          ).length;
          const totalChildren = parentGroup.categories.length;

          return (
            <ParentGroupItem
              key={parentGroup.id}
              parentGroup={parentGroup}
              initialExcludedChildrenCount={excludedChildrenCount}
              totalChildren={totalChildren}
              initialExcludedCategories={initialFormikValues.excludedCategories}
              onCategoryToggle={onCategoryToggle}
              onParentGroupToggle={onParentGroupToggle}
            />
          );
        })}
      </>
    );
  },
  (prevProps, nextProps) => {
    // Re-render when Formik values change to update checkbox states
    // Compare arrays by reference - if they're different, re-render
    return (
      prevProps.treeStructure === nextProps.treeStructure &&
      prevProps.initialFormikValues.excludedCategories ===
        nextProps.initialFormikValues.excludedCategories &&
      prevProps.onCategoryToggle === nextProps.onCategoryToggle &&
      prevProps.onParentGroupToggle === nextProps.onParentGroupToggle
    );
  },
);
RenderCategoriesTree.displayName = 'RenderCategoriesTree';
